import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";

const Tooltip = ({
    content,
    children,
    offset = 8,
    verticalOffset = -20,
    className = "",
    parentClass = "",
    position = "right",
    disabled = false
}) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    const wrapperRef = useRef(null);
    const tooltipRef = useRef(null);
    const rafRef = useRef(null);

    const updatePosition = () => {
        if (!wrapperRef.current || !tooltipRef.current) return;

        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();

        let top;
        let left;

        if (position === "top") {
            top = wrapperRect.top + window.scrollY - tooltipRect.height - offset;
            left = wrapperRect.left + window.scrollX + (wrapperRect.width / 2) - (tooltipRect.width / 2);
        } else {
            top =
                wrapperRect.top +
                wrapperRect.height / 2 -
                tooltipRect.height / 2 +
                window.scrollY +
                verticalOffset;
            const fitsRight =
                wrapperRect.right + tooltipRect.width + offset < window.innerWidth;

            if (fitsRight) {
                left = wrapperRect.right + window.scrollX + offset;
            } else {
                left = wrapperRect.left - tooltipRect.width + window.scrollX - offset;
            }
        }

        const viewportTop = window.scrollY;
        const viewportBottom = window.scrollY + window.innerHeight;

        if (top < viewportTop) top = viewportTop + offset;
        if (top + tooltipRect.height > viewportBottom) {
            top = viewportBottom - tooltipRect.height - offset;
        }

        setCoords({ top, left });
    };

    const scheduleUpdate = () => {
        if (rafRef.current) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            updatePosition();
        });
    };

    useEffect(() => {
        if (!visible) return;

        updatePosition();
        window.addEventListener("resize", scheduleUpdate);
        window.addEventListener("scroll", scheduleUpdate, true);

        return () => {
            window.removeEventListener("resize", scheduleUpdate);
            window.removeEventListener("scroll", scheduleUpdate, true);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [visible]);

    return (
        <span
            ref={wrapperRef}
            className={`inline-flex ${parentClass}`}
            onMouseEnter={() => !disabled && setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onContextMenu={() => setVisible(false)}
            onFocus={() => setVisible(true)}
            onBlur={() => setVisible(false)}
            tabIndex={0}
        >
            {children}

            {visible && content && !disabled &&
                ReactDOM.createPortal(
                    <div
                        ref={tooltipRef}
                        role="tooltip"
                        style={{
                            position: "absolute",
                            top: coords.top,
                            left: coords.left,
                            zIndex: 99999,
                            pointerEvents: "none", // 🔑 prevents flicker
                        }}
                        className={`px-2 py-1 bg-gray-600 text-gray-100 rounded shadow-lg text-xs whitespace-nowrap ${className}`}
                    >
                        {content}
                    </div>,
                    document.body
                )}
        </span>
    );
};

export default Tooltip;
